import { useState, useEffect } from 'react';
import { getInitialCurrencyState } from '../util/currency';
import CUSTOM_EVENT from '../util/customEvents';
/**
 * Custom hook for managing currency states based on changes detected via events.
 * FUNCTIONAL components only.
 * It listens for currency changes, updates the state accordingly, and returns
 * the current currency settings as a tuple containing the current currency code and symbol,
 * along with the current price settings.
 *
 * @param {Array<Object>} pricePerPersonPerCurrency - Array of currency settings, each containing:
 *   @param {string} pricePerPersonPerCurrency.currency - Currency code, e.g., 'USD', 'EUR'.
 *   @param {string} pricePerPersonPerCurrency.formattedPrice - Formatted price string, e.g., '1,000.00'.
 *   @param {number} pricePerPersonPerCurrency.price - Numeric price, e.g., 1000.
 * @returns {Array} A tuple:
 *   - {Object} The current currency code and symbol (currentCurrencyCodeAndSymbol), which includes:
 *     - {string} code - The current currency code.
 *     - {string} symbol - The current currency symbol.
 *   - {Object} The current price settings based on the active currency (currencyPriceSetting), which includes:
 *     - {string} currency - Active currency code.
 *     - {string} formattedPrice - Formatted price for the active currency.
 *     - {number} price - Numeric price for the active currency.
 */
function useCurrency(pricePerPersonPerCurrency) {
  const [currentCurrencyCodeAndSymbol, setCurrentCurrencyCodeAndSymbol] = useState(
    getInitialCurrencyState(pricePerPersonPerCurrency)
  );
  const [currencyPriceSetting, setCurrencyPriceSetting] = useState(pricePerPersonPerCurrency[0]);

  useEffect(() => {
    const handleCurrencyChange = (event) => {
      const newCurrencyCode = event?.detail?.code;
      if (newCurrencyCode) setCurrentCurrencyCodeAndSymbol(event.detail);
      else console.error('Currency data not found for:', newCurrencyCode);
    };

    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, handleCurrencyChange);
    return () => {
      window.removeEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, handleCurrencyChange);
    };
  }, []);

  useEffect(() => {
    const newCurrencyPriceSettings = pricePerPersonPerCurrency.find(
      (currencyItem) => currencyItem.currency === currentCurrencyCodeAndSymbol.code
    );
    if (newCurrencyPriceSettings) {
      setCurrencyPriceSetting(newCurrencyPriceSettings);
    } else {
      console.error('Currency not found from current currency chosen to currency from model');
    }
  }, [currentCurrencyCodeAndSymbol, pricePerPersonPerCurrency]);

  return [currentCurrencyCodeAndSymbol, currencyPriceSetting];
}

export default useCurrency;
